import { useEffect, useMemo, useState, DragEvent } from 'react';
import { hasPermission } from '@/types';
import { NavLink } from 'react-router-dom';
import { tasks, users, type AssignableUser } from '@/api/endpoints';
import type { Task, TaskStatus, Priority } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { TaskFormModal, PRIORITY_LABELS } from '@/components/TaskFormModal';
import { STUDY_TYPE_LABELS, studyTypeGroup } from '@/lib/studyTypeGroups';

const COLUMNS: { key: TaskStatus; label: string; color: string }[] = [
  { key: 'TODO', label: 'Do zrobienia', color: 'bg-ink-400' },
  { key: 'IN_PROGRESS', label: 'W realizacji', color: 'bg-water-500' },
  { key: 'AI_VERIFICATION', label: 'Weryfikacja AI', color: 'bg-ai-500' },
  { key: 'HUMAN_VERIFICATION', label: 'Weryfikacja człowiek', color: 'bg-amber-500' },
  { key: 'COMPLETED', label: 'Zakończone', color: 'bg-green-500' },
];

function prioritoBorder(p: string): string {
  if (p === 'CRITICAL') return 'border-l-red-500';
  if (p === 'HIGH') return 'border-l-amber-500';
  if (p === 'LOW') return 'border-l-ink-100';
  return 'border-l-ink-200';
}

export function Tasks() {
  const { user } = useAuth();
  const canManage = hasPermission(user, 'TASK_EDIT');

  const [board, setBoard] = useState<Record<string, Task[]> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** Zadania, które zalogowany może przyjąć — liczone na serwerze z jego profilu (typy opracowań). */
  const [doPrzyjecia, setDoPrzyjecia] = useState<Set<number>>(new Set());
  const [przyjmuje, setPrzyjmuje] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);

  // filtry
  const [fAssignee, setFAssignee] = useState<string>('');
  const [fPriority, setFPriority] = useState<string>('');
  const [fStudy, setFStudy] = useState<string>('');
  const [mineOnly, setMineOnly] = useState(false);
  const [toAcceptOnly, setToAcceptOnly] = useState(false);

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);

  const load = () => {
    setErr(null);
    tasks.kanban().then(setBoard).catch(e => setErr(String(e)));
    tasks.available().then(l => setDoPrzyjecia(new Set(l.map(t => t.id)))).catch(() => { /* niekrytyczne */ });
  };

  const przyjmij = (z: Task) => {
    setPrzyjmuje(z.id); setNotice(null);
    tasks.accept(z.id)
      .then(() => load())
      // 409 — ktoś przyjął wcześniej; odświeżamy, żeby karta pokazała, kto.
      .catch(e => { setNotice(e?.message ?? String(e)); load(); })
      .finally(() => setPrzyjmuje(null));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    users.assignable().then(setAssignableUsers).catch(() => { /* niekrytyczne */ });
  }, []);

  // Lista opracowań obecnych w zadaniach (do filtra) — bez dodatkowego zapytania.
  const studyOptions = useMemo(() => {
    if (!board) return [];
    const map = new Map<number, string>();
    Object.values(board).flat().forEach(t => {
      if (t.studyId != null && t.studyNumber) map.set(t.studyId, t.studyNumber);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [board]);

  const filtersActive = !!fAssignee || !!fPriority || !!fStudy || mineOnly || toAcceptOnly;

  const matchesFilters = (t: Task): boolean => {
    if (mineOnly && t.assigneeId !== user?.id) return false;
    if (toAcceptOnly && !doPrzyjecia.has(t.id)) return false;
    if (fAssignee === '__none__' && t.assigneeId != null) return false;
    if (fAssignee && fAssignee !== '__none__' && String(t.assigneeId) !== fAssignee) return false;
    if (fPriority && t.priority !== fPriority) return false;
    if (fStudy && String(t.studyId) !== fStudy) return false;
    return true;
  };

  const onDragStart = (e: DragEvent<HTMLDivElement>, id: number) => {
    setDragging(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e: DragEvent<HTMLDivElement>, targetStatus: TaskStatus) => {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData('text/plain') || dragging);
    setDragging(null);
    if (!id || !board) return;
    const currentColumn = Object.entries(board).find(([, arr]) => arr.some(z => z.id === id));
    if (currentColumn && currentColumn[0] === targetStatus) return;
    const targetCount = board[targetStatus]?.length ?? 0;
    void tasks.move(id, targetStatus, targetCount)
      .then(() => load())
      .catch(e2 => setErr(String(e2)));
  };

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (t: Task) => { setEditing(t); setModalOpen(true); };

  const clearFilters = () => { setFAssignee(''); setFPriority(''); setFStudy(''); setMineOnly(false); setToAcceptOnly(false); };

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!board) return <div className="text-ink-400">Ładowanie…</div>;

  const totalVisible = Object.values(board).flat().filter(matchesFilters).length;

  return (
    <div className="max-w-[1600px]">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1>Tablica zadań</h1>
          <div className="text-ink-400 text-[13px]">
            Kanban — przeciągnij karty między kolumnami{canManage && ', kliknij ołówek aby edytować'}.
          </div>
        </div>
        <button onClick={openNew} className="btn btn-primary">
          <Icon name="plus" className="w-4 h-4" /> Nowe zadanie
        </button>
      </header>

      {/* Pasek filtrów */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setMineOnly(m => !m)}
          className={'btn btn-sm ' + (mineOnly ? 'btn-primary' : 'btn-secondary')}>
          <Icon name="check" className="w-3.5 h-3.5" /> Moje zadania
        </button>
        <button
          onClick={() => setToAcceptOnly(m => !m)}
          title="Nieprzypisane zadania z typów opracowań, które wykonujesz (profil użytkownika)"
          className={'btn btn-sm ' + (toAcceptOnly ? 'btn-primary' : 'btn-secondary')}>
          Do przyjęcia
          <span className={'ml-1 px-1.5 rounded-full text-[11px] font-mono ' +
            (doPrzyjecia.size > 0 && !toAcceptOnly ? 'bg-water-500 text-white' : '')}>{doPrzyjecia.size}</span>
        </button>
        <SearchableSelect value={fAssignee} onChange={setFAssignee} className="select-sm" placeholder="Osoba">
          <option value="">Wszyscy przypisani</option>
          <option value="__none__">— nieprzypisane —</option>
          {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
        </SearchableSelect>
        <SearchableSelect value={fPriority} onChange={setFPriority} className="select-sm" placeholder="Priorytet">
          <option value="">Każdy priorytet</option>
          {(Object.keys(PRIORITY_LABELS) as Priority[]).map(p => (
            <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
          ))}
        </SearchableSelect>
        {studyOptions.length > 0 && (
          <SearchableSelect value={fStudy} onChange={setFStudy} className="select-sm"
            placeholder="Opracowanie" searchPlaceholder="Szukaj…">
            <option value="">Wszystkie opracowania</option>
            {studyOptions.map(([id, num]) => <option key={id} value={id}>{num}</option>)}
          </SearchableSelect>
        )}
        {filtersActive && (
          <>
            <span className="text-[12px] text-ink-400">{totalVisible} widocznych</span>
            <button onClick={clearFilters} className="btn btn-secondary btn-sm">
              <Icon name="x" className="w-3.5 h-3.5" /> Wyczyść
            </button>
          </>
        )}
      </div>

      {notice && (
        <div className="mb-3 p-2.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[13px] flex items-center gap-2">
          {notice}
          <button onClick={() => setNotice(null)} className="ml-auto btn btn-ghost btn-icon"><Icon name="x" className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <div className="grid grid-cols-5 gap-3">
        {COLUMNS.map(col => {
          const items = (board[col.key] ?? []).filter(matchesFilters);
          return (
            <div
              key={col.key}
              onDragOver={onDragOver}
              onDrop={e => onDrop(e, col.key)}
              className="flex flex-col bg-ink-50 rounded-lg p-2 min-h-[400px]"
            >
              <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
                <span className={'w-2 h-2 rounded-full ' + col.color}/>
                <span className="text-[12px] font-semibold uppercase tracking-wider text-ink-700">{col.label}</span>
                <span className="ml-auto text-[11px] text-ink-400 font-mono">{items.length}</span>
              </div>
              <div className="flex flex-col gap-2">
                {items.map(z => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const dl = z.deadline ? new Date(z.deadline) : null;
                  const isOverdue = z.status !== 'COMPLETED' && dl != null && dl < today;
                  const doPrzyjeciaDlaMnie = doPrzyjecia.has(z.id);
                  const grupa = studyTypeGroup(z.studyType);
                  return (
                    <div
                      key={z.id}
                      draggable
                      onDragStart={e => onDragStart(e, z.id)}
                      className={'group relative bg-white rounded-md p-2.5 shadow-sm border border-ink-100 border-l-[3px] cursor-grab active:cursor-grabbing ' + prioritoBorder(z.priority) + (dragging === z.id ? ' opacity-50' : '') + (doPrzyjeciaDlaMnie ? ' ring-1 ring-water-400' : '')}
                    >
                      {canManage && (
                        <button
                          onClick={e => { e.stopPropagation(); openEdit(z); }}
                          className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-ink-400 hover:text-water-600 p-0.5"
                          title="Edytuj zadanie">
                          <Icon name="edit" className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {z.studyNumber && (
                        <div className="font-mono text-[10px] text-ink-500 mb-1">
                          {z.studyId ? (
                            <NavLink to={`/studies/${z.studyId}`} onClick={e => e.stopPropagation()} className="text-ink-500 hover:text-ink-800">
                              {z.studyNumber}
                            </NavLink>
                          ) : z.studyNumber}
                        </div>
                      )}
                      <div className="text-[13px] font-medium text-ink-900 leading-snug mb-2 pr-4">{z.title}</div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-water-500 to-water-700 text-white grid place-items-center text-[10px] font-semibold"
                          title={z.assigneeFullName ?? 'nieprzypisane'}>
                          {z.assigneeInitials ?? '?'}
                        </div>
                        {z.deadline && (
                          <span className={'text-[11px] font-mono ' + (isOverdue ? 'text-red-500 font-bold' : 'text-ink-500')}>
                            {z.deadline}{isOverdue && ' ⚠'}
                          </span>
                        )}
                        {grupa && (
                          <span className="pill pill-water text-[10px] ml-auto" title={STUDY_TYPE_LABELS[z.studyType!] ?? grupa.label}>
                            {grupa.key}
                          </span>
                        )}
                        {z.stepCode && <span className={'pill pill-earth' + (grupa ? '' : ' ml-auto')}>{z.stepCode}</span>}
                      </div>
                      {doPrzyjeciaDlaMnie && (
                        <button
                          onClick={e => { e.stopPropagation(); przyjmij(z); }}
                          disabled={przyjmuje === z.id}
                          className="btn btn-primary btn-sm w-full mt-2 justify-center">
                          {przyjmuje === z.id ? 'Przyjmuję…' : 'Przyjmij zadanie'}
                        </button>
                      )}
                    </div>
                  );
                })}
                {items.length === 0 && (
                  <div className="text-[11px] text-ink-400 text-center py-6 border border-dashed border-ink-200 rounded-md">
                    {filtersActive ? 'Brak pasujących' : 'Upuść task'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {user && (
        <TaskFormModal
          open={modalOpen}
          initial={editing}
          canManage={canManage}
          currentUserId={user.id}
          currentUserName={user.fullName}
          onClose={() => setModalOpen(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
